/* SUMMARY
  * Imports
  * getCompInfoLabel: translated label of a compInfo
  * missingCompInfoLabels: labels of the required compInfos left empty
  * compInfoFieldName: field name entry naming those compInfos in a form error
*/

/* Imports */
import { validator } from "../../../services/form-validator/form-validator";
import { allowedCompInfoUsages, isCompInfoUsable } from "./compinfo-usage";
/***/

/**
* getCompInfoLabel: translated label of a compInfo
* @param compInfo - compInfo descriptor
* @return - label in the user language, falling back on the default one
*/
export const getCompInfoLabel = (compInfo: EasyTech.CompInfo): string => {
  const currentLanguage = localStorage.getItem("i18nextLng") || "en";

  if (compInfo.translations && Array.isArray(compInfo.translations)) {
    const translation = compInfo.translations.find(
      (translation) => translation.language?.isoCode === currentLanguage
    );

    if (translation && translation.label) return translation.label;
  }

  return compInfo.label || "";
};
/***/

/**
* missingCompInfoLabels: labels of the required compInfos left empty
*
* Même périmètre que compInfoValidatorFor : seuls les compInfos réellement affichés à ce statut
* comptent, sinon le message nommerait un champ que l'utilisateur ne peut pas atteindre.
*
* @param compInfos - compinfos of the workorder being submitted
* @param status - current workorder status, absent while creating one
* @return - labels to show, in the order the form displays them
*/
export const missingCompInfoLabels = (compInfos?: EasyTech.CompInfos, status?: string): string[] => {
  if (!compInfos) return [];
  const usages = allowedCompInfoUsages(status);

  const missingInputs = (compInfos.inputs || [])
    .filter((el) => isCompInfoUsable(el.compInfo, usages) && !validator.isValidCompInfo(el.compInfo, el.value));

  const missingSelects = (compInfos.selects || [])
    .filter((el) => isCompInfoUsable(el.compInfo, usages)
      && !validator.isValidSelectCompInfo(el.compInfo, (el.values || []).map((value) => value.value)));

  return [...missingInputs, ...missingSelects]
    .sort((a, b) => (a.compInfo.order ?? Infinity) - (b.compInfo.order ?? Infinity))
    .map((el) => getCompInfoLabel(el.compInfo))
    .filter((label) => label !== "");
};
/***/

/**
* compInfoFieldName: field name entry naming those compInfos in a form error
*
* Le bloc « Informations complémentaires » porte une quinzaine de champs : sans les nommer, le
* message d'erreur laisse l'utilisateur déplier la section et chercher les bordures rouges.
*
* @param t - translation function
* @return - a FieldNames entry, to put under the compInfos key
*/
export const compInfoFieldName = (t: (key: string) => string) => (data: EasyTech.Workorder): string => {
  const label = t("common:compInfos");
  const missing = missingCompInfoLabels(data?.compInfos, data?.status);

  return missing.length > 0 ? `${label} (${missing.join(", ")})` : label;
};
/***/
